import { createContext, ReactNode, useContext, useMemo, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { IClientViewModel } from 'viewModels';
import { IUserAuth } from 'entities';
import { CompanyController } from '../controller/CompanyController';
import { useAuth } from './auth';
import { TypeUser } from '../types/enums';

type ProviderProps = {
  children: ReactNode;
};

type ClientsContextProps = {
  clients: IClientViewModel[];
  setClients: React.Dispatch<React.SetStateAction<IClientViewModel[]>>;
  setEnableClientsLoad: React.Dispatch<React.SetStateAction<boolean>>;
  isLoadingClients: boolean;
};

const ClientsContext = createContext<ClientsContextProps>({} as ClientsContextProps);

const ClientsProvider = ({ children }: ProviderProps) => {
  const { user, tokenIsValid, setUser } = useAuth();
  const [enableClientsLoad, setEnableClientsLoad] = useState<boolean>(false);
  const [clients, setClients] = useState<IClientViewModel[]>([] as IClientViewModel[]);

  const { isLoading: isLoadingClients } = useQuery(
    'clients',
    () => CompanyController.getClientByUserId(user.id),
    {
      onSuccess: (data) => {
        setClients(data);
      },
      refetchOnWindowFocus: false,
      enabled:
        !!user.id &&
        [TypeUser.COMPANY, TypeUser.ADM, TypeUser.USER].includes(user.type) &&
        tokenIsValid &&
        enableClientsLoad,
    },
  );

  useQuery(
    ['clientsCollaborator', user?.collaboratorInfo?.profileId],
    () => CompanyController.getClientsByProfileId(user?.collaboratorInfo?.profileId as string),
    {
      onSuccess: (data) => {
        setClients(data);
        setUser((prev) => {
          return {
            ...prev,
            collaboratorInfo: {
              ...prev.collaboratorInfo,
              profileInfo: {
                ...prev.collaboratorInfo?.profileInfo,
                clientsId: data.map((client) => client.id),
              },
            },
          } as IUserAuth;
        });
      },
      refetchOnWindowFocus: false,
      enabled:
        user.collaboratorInfo?.profileId !== undefined &&
        user.type === TypeUser.COLLABORATOR &&
        tokenIsValid &&
        enableClientsLoad,
    },
  );
  useEffect(() => {
    if (!tokenIsValid) {
      setClients([]);
    }
  }, [tokenIsValid]);

  const contextValues = useMemo(
    () => ({ clients, setClients, isLoadingClients, setEnableClientsLoad }),
    [clients, isLoadingClients],
  );
  return <ClientsContext.Provider value={contextValues}>{children}</ClientsContext.Provider>;
};

export default ClientsProvider;

export const useClients: () => ClientsContextProps = () => useContext(ClientsContext);
